import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

// ============================================
// TYPES
// ============================================

export type BowelConsistency = 'VERY_HARD' | 'HARD' | 'NORMAL' | 'SOFT' | 'LOOSE' | 'LIQUID';
export type BowelAmount = 'SMALL' | 'MEDIUM' | 'LARGE';
export type NoteMood = 'great' | 'good' | 'okay' | 'bad' | 'terrible';

export interface BowelLog {
  id: string;
  userId: string;
  date: string;
  time?: string;
  consistency: BowelConsistency;
  amount?: BowelAmount;
  color?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DailyNote {
  id: string;
  userId: string;
  date: string;
  content: string;
  mood?: NoteMood;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBowelLogData {
  date: string;
  time?: string;
  consistency: BowelConsistency;
  amount?: BowelAmount;
  color?: string;
  notes?: string;
}

export interface UpsertDailyNoteData {
  date: string;
  content: string;
  mood?: NoteMood;
}

// ============================================
// BOWEL LOG SERVICE
// ============================================

export const bowelService = {
  async create(data: CreateBowelLogData): Promise<BowelLog> {
    const res = await axios.post(`${API_URL}/api/journal/bowel`, data, {
      headers: getHeaders(),
    });
    return res.data.log;
  },

  async getAll(params?: { startDate?: string; endDate?: string; limit?: number; offset?: number }): Promise<{ logs: BowelLog[]; total: number }> {
    const res = await axios.get(`${API_URL}/api/journal/bowel`, {
      headers: getHeaders(),
      params,
    });
    return res.data;
  },

  async getToday(): Promise<{ logs: BowelLog[]; count: number }> {
    const res = await axios.get(`${API_URL}/api/journal/bowel/today`, {
      headers: getHeaders(),
    });
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await axios.delete(`${API_URL}/api/journal/bowel/${id}`, {
      headers: getHeaders(),
    });
  },
};

// ============================================
// DAILY NOTE SERVICE
// ============================================

export const noteService = {
  async upsert(data: UpsertDailyNoteData): Promise<DailyNote> {
    const res = await axios.post(`${API_URL}/api/journal/notes`, data, {
      headers: getHeaders(),
    });
    return res.data.note;
  },

  async getAll(params?: { startDate?: string; endDate?: string; limit?: number; offset?: number }): Promise<{ notes: DailyNote[]; total: number }> {
    const res = await axios.get(`${API_URL}/api/journal/notes`, {
      headers: getHeaders(),
      params,
    });
    return res.data;
  },

  async getToday(): Promise<DailyNote | null> {
    const res = await axios.get(`${API_URL}/api/journal/notes/today`, {
      headers: getHeaders(),
    });
    return res.data.note;
  },

  async getByDate(date: string): Promise<DailyNote | null> {
    const res = await axios.get(`${API_URL}/api/journal/notes/${date}`, {
      headers: getHeaders(),
    });
    return res.data.note;
  },

  async delete(id: string): Promise<void> {
    await axios.delete(`${API_URL}/api/journal/notes/${id}`, {
      headers: getHeaders(),
    });
  },
};

// ============================================
// HELPERS
// ============================================

export const consistencyLabels: Record<BowelConsistency, string> = {
  VERY_HARD: 'Foarte dur (Tip 1)',
  HARD: 'Dur (Tip 2)',
  NORMAL: 'Normal (Tip 3-4)',
  SOFT: 'Moale (Tip 5)',
  LOOSE: 'Fără formă (Tip 6)',
  LIQUID: 'Lichid (Tip 7)',
};

export const consistencyEmoji: Record<BowelConsistency, string> = {
  VERY_HARD: '🪨',
  HARD: '🟤',
  NORMAL: '✅',
  SOFT: '🟡',
  LOOSE: '🟠',
  LIQUID: '💧',
};

export const amountLabels: Record<BowelAmount, string> = {
  SMALL: 'Puțin',
  MEDIUM: 'Mediu',
  LARGE: 'Mult',
};

export const moodLabels: Record<NoteMood, string> = {
  great: 'Excelent 😄',
  good: 'Bine 🙂',
  okay: 'Ok 😐',
  bad: 'Rău 😟',
  terrible: 'Foarte rău 😰',
};

export const moodEmoji: Record<NoteMood, string> = {
  great: '😄',
  good: '🙂',
  okay: '😐',
  bad: '😟',
  terrible: '😰',
};

export const getLocalDateString = (d = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
